import { validateResourceList, type ManagedResource } from '../../../shared/resource-catalog.mjs';
import type { ResourceDefinition } from '../../domain/types';
import { resourceCategoryLabel } from './categories';
import { listLocalResources, subscribeLocalResources } from './localResourceStore';

export type PublicResourceMode = 'live' | 'local' | 'mirror';
export type PublicResourceState = {
  items: ResourceDefinition[];
  status: 'loading' | 'ready' | 'error';
  mode: PublicResourceMode;
  error?: string;
  total?: number;
  limit?: number;
};
export type PublicResourceRepository = {
  mode: PublicResourceMode;
  list(): Promise<{ items: ResourceDefinition[]; total: number; limit: number }>;
  subscribe(listener: () => void): () => void;
};

export function publishedResourceItems(items: ManagedResource[], mode: PublicResourceMode = 'local'): ResourceDefinition[] {
  return items.filter(item => item.status === 'published').map(item => ({
    id: item.id,
    slug: `managed-${item.id}`,
    kind: 'resource',
    resourceCategory: item.category,
    title: item.title,
    summary: item.note,
    category: resourceCategoryLabel(item.category),
    tags: [resourceCategoryLabel(item.category)],
    icon: item.category === 'image' ? 'image' : item.category === 'vector' ? 'pen' : 'file',
    managed: true,
    thumbnailUrl: item.thumbnailUrl,
    downloadUrl: mode === 'local' ? item.downloadUrl : `${mode === 'mirror' ? 'https://designforge.site' : ''}/api/resource-downloads/${encodeURIComponent(item.id)}`,
  }));
}

/** Only this adapter knows where the public catalog comes from. Files stay external. */
export function createPublicResourceRepository(fetcher: typeof fetch = fetch): PublicResourceRepository {
  const local = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
  const live = ['designforge.site', 'designforge-8km.pages.dev'].includes(window.location.hostname);
  const mode: PublicResourceMode = local ? 'local' : live ? 'live' : 'mirror';
  return {
    mode,
    async list() {
      if (local) {
        const result = await listLocalResources(true);
        const items = publishedResourceItems(result.items);
        return { items, total: result.total > result.items.length ? result.total : items.length, limit: result.limit };
      }
      let response: Response;
      try {
        response = await fetcher(live ? '/api/resources' : 'https://designforge.site/api/resources', {
          method: 'GET', credentials: 'omit', redirect: 'error', cache: 'no-store', headers: { Accept: 'application/json' },
        });
      } catch {
        throw new Error('Không kết nối được kho tài nguyên. Hãy thử lại.');
      }
      if (!response.ok) throw new Error('Không tải được kho tài nguyên. Hãy thử lại.');
      const result = validateResourceList(await response.json(), { publicOnly: true });
      return { items: publishedResourceItems(result.items, mode), total: result.total, limit: result.limit };
    },
    subscribe(listener) { return local ? subscribeLocalResources(listener) : () => {}; },
  };
}
